/**
 * AnnotationsTool Component - WESTEER Design System
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/map-tools/annotations
 *
 * @description
 * Herramienta para agregar anotaciones y notas en el mapa.
 * Permite marcar ubicaciones con comentarios temporales o persistentes.
 *
 * @remarks
 * **Características**:
 * - Click en mapa para agregar anotación
 * - Modal para escribir texto
 * - Visualización con iconos sticky note
 * - Panel lateral con lista de anotaciones
 * - Editar y eliminar anotaciones
 * - Persistencia en localStorage
 * - Navegación a anotación desde lista
 *
 * @example
 * ```tsx
 * import { AnnotationsTool } from '@/core/map-tools/annotations';
 *
 * function MapTools() {
 *   const [active, setActive] = useState(false);
 *
 *   return (
 *     <AnnotationsTool
 *       map={mapInstance}
 *       isActive={active}
 *       onDeactivate={() => setActive(false)}
 *     />
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { MapLayerMouseEvent, MapMouseEvent } from 'maplibre-gl';
import {
  StickyNote,
  X,
  Trash2,
  Edit2,
  MapPin,
  Save,
  Lightbulb,
  CheckSquare,
  AlertCircle,
  AlertTriangle,
  Search,
  Calendar,
  Tag,
  Copy,
  Navigation,
  Loader2
} from 'lucide-react';
import {
  type Annotation,
  type AnnotationCategory,
  type AnnotationsToolProps,
  ANNOTATION_CATEGORIES
} from './types';
import { ConfirmModal } from '../../components/ConfirmModal';
import { SuccessToast } from '../../components/SuccessToast';
import './annotations.css';

// Re-export para compatibilidad
export type { Annotation } from './types';

/**
 * AnnotationsTool - Herramienta de anotaciones
 */
export function AnnotationsTool({
  map,
  isActive,
  onDeactivate,
}: AnnotationsToolProps): JSX.Element | null {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  const [annotationText, setAnnotationText] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<AnnotationCategory>('note');
  const [tempCoordinates, setTempCoordinates] = useState<[number, number] | null>(null);

  // Estados para ConfirmModal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState<boolean>(false);
  const [deletingAnnotationId, setDeletingAnnotationId] = useState<string | null>(null);

  // Estados para búsqueda y filtrado
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<AnnotationCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'category'>('date');

  // Estados para feedback visual
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [copiedCoordId, setCopiedCoordId] = useState<string | null>(null);

  /**
   * Filtrado y ordenamiento de anotaciones
   */
  const filteredAnnotations = useMemo(() => {
    let result = [...annotations];

    // Búsqueda por texto
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.text.toLowerCase().includes(query)
      );
    }

    // Filtro por categoría
    if (filterCategory !== 'all') {
      result = result.filter(a => a.category === filterCategory);
    }

    // Ordenamiento
    if (sortBy === 'date') {
      result.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    } else {
      result.sort((a, b) => a.category.localeCompare(b.category));
    }

    return result;
  }, [annotations, searchQuery, filterCategory, sortBy]);

  /**
   * Función para copiar coordenadas al portapapeles con feedback visual
   */
  const copyCoordinates = useCallback((coordinates: [number, number], annotationId: string) => {
    const text = `${coordinates[0].toFixed(6)}, ${coordinates[1].toFixed(6)}`;
    navigator.clipboard.writeText(text).then(() => {
      console.log('[AnnotationsTool] Coordenadas copiadas:', text);
      setSuccessMessage('Coordenadas copiadas al portapapeles');
      setCopiedCoordId(annotationId);

      // Limpiar feedback después de 2s
      setTimeout(() => {
        setCopiedCoordId(null);
      }, 2000);
    }).catch((err) => {
      console.error('[AnnotationsTool] Error al copiar:', err);
    });
  }, []);

  /**
   * Formatear fecha relativa
   */
  const formatRelativeTime = useCallback((date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'ahora';
    if (diffMins < 60) return `hace ${diffMins}min`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    if (diffDays < 7) return `hace ${diffDays}d`;
    return date.toLocaleDateString();
  }, []);

  /**
   * Cargar anotaciones desde localStorage al montar
   */
  useEffect(() => {
    const savedAnnotations =
      localStorage.getItem('wayline-annotations') ??
      (() => {
        const legacy = localStorage.getItem('westeer-annotations');
        if (legacy) {
          localStorage.setItem('wayline-annotations', legacy);
          localStorage.removeItem('westeer-annotations');
        }
        return legacy;
      })();
    if (savedAnnotations) {
      try {
        const parsed = JSON.parse(savedAnnotations);
        // Convertir strings de fecha a Date objects y agregar categoría por defecto si no existe
        const annotationsWithDates = parsed.map((a: any) => ({
          ...a,
          category: a.category || 'note', // Fallback para anotaciones antiguas
          createdAt: new Date(a.createdAt),
          updatedAt: new Date(a.updatedAt),
        }));
        setAnnotations(annotationsWithDates);
      } catch (error) {
        console.error('[AnnotationsTool] Error loading annotations:', error);
      }
    }
  }, []);

  /**
   * Guardar anotaciones en localStorage cuando cambian
   */
  useEffect(() => {
    if (annotations.length > 0) {
      localStorage.setItem('wayline-annotations', JSON.stringify(annotations));
    }
  }, [annotations]);

  /**
   * Renderizar anotaciones en el mapa
   */
  useEffect(() => {
    if (!map || annotations.length === 0) return;

    // Agregar source si no existe
    if (!map.getSource('annotations')) {
      map.addSource('annotations', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });

      // Crear iconos para cada categoría si no existen
      Object.values(ANNOTATION_CATEGORIES).forEach((category) => {
        const iconName = `annotation-icon-${category.id}`;
        if (!map.hasImage(iconName)) {
          const size = 48;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            // Limpiar canvas
            ctx.clearRect(0, 0, size, size);

            // Sombra suave
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowBlur = 6;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;

            // Forma del sticky note con esquina doblada
            ctx.fillStyle = category.color;
            ctx.beginPath();
            ctx.moveTo(6, 6);
            ctx.lineTo(size - 12, 6);
            ctx.lineTo(size - 6, 12); // Esquina doblada
            ctx.lineTo(size - 6, size - 6);
            ctx.lineTo(6, size - 6);
            ctx.closePath();
            ctx.fill();

            // Resetear sombra para borde y esquina
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;

            // Esquina doblada (efecto 3D)
            ctx.fillStyle = category.borderColor;
            ctx.beginPath();
            ctx.moveTo(size - 12, 6);
            ctx.lineTo(size - 6, 12);
            ctx.lineTo(size - 12, 12);
            ctx.closePath();
            ctx.fill();

            // Borde
            ctx.strokeStyle = category.borderColor;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(6, 6);
            ctx.lineTo(size - 12, 6);
            ctx.lineTo(size - 6, 12);
            ctx.lineTo(size - 6, size - 6);
            ctx.lineTo(6, size - 6);
            ctx.closePath();
            ctx.stroke();

            // Líneas decorativas (3 líneas horizontales)
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
            ctx.lineWidth = 1.5;
            [16, 22, 28].forEach((y) => {
              ctx.beginPath();
              ctx.moveTo(12, y);
              ctx.lineTo(size - 12, y);
              ctx.stroke();
            });

            map.addImage(iconName, {
              width: size,
              height: size,
              data: ctx.getImageData(0, 0, size, size).data as unknown as Uint8Array,
            });
          }
        }
      });

      map.addLayer({
        id: 'annotations-layer',
        type: 'symbol',
        source: 'annotations',
        layout: {
          'icon-image': [
            'concat',
            'annotation-icon-',
            ['coalesce', ['get', 'category'], 'note']
          ],
          'icon-size': 0.7,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Click handler para mostrar detalle
      map.on('click', 'annotations-layer', (e: MapLayerMouseEvent) => {
        if (e.features && e.features.length > 0) {
          const annotationId = e.features[0].properties?.id;
          const annotation = annotations.find((a) => a.id === annotationId);
          if (annotation) {
            handleEditAnnotation(annotation);
          }
        }
      });

      // Cambiar cursor al hacer hover
      map.on('mouseenter', 'annotations-layer', () => {
        if (map) map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'annotations-layer', () => {
        if (map) map.getCanvas().style.cursor = '';
      });
    }

    // Actualizar features
    const source = map.getSource('annotations') as maplibregl.GeoJSONSource;
    source?.setData({
      type: 'FeatureCollection',
      features: annotations.map((a) => ({
        type: 'Feature',
        properties: {
          id: a.id,
          text: a.text,
          category: a.category,
        },
        geometry: {
          type: 'Point',
          coordinates: a.coordinates,
        },
      })),
    });
  }, [map, annotations]);

  /**
   * Manejar click en el mapa para agregar anotación
   */
  useEffect(() => {
    if (!map || !isActive) return;

    const handleMapClick = (e: MapMouseEvent): void => {
      // Solo si no hay modal abierto
      if (showModal) return;

      setTempCoordinates([e.lngLat.lng, e.lngLat.lat]);
      setAnnotationText('');
      setSelectedCategory('note'); // Categoría por defecto
      setEditingAnnotation(null);
      setShowModal(true);
    };

    map.on('click', handleMapClick);

    return (): void => {
      map.off('click', handleMapClick);
    };
  }, [map, isActive, showModal]);

  /**
   * Limpiar al desactivar
   */
  useEffect(() => {
    if (!isActive && map) {
      // No eliminamos las anotaciones al desactivar, solo ocultamos la herramienta
      setShowModal(false);
      setEditingAnnotation(null);
      setTempCoordinates(null);
    }
  }, [isActive, map]);

  /**
   * Guardar anotación
   */
  const handleSaveAnnotation = (): void => {
    if (!annotationText.trim()) return;
    if (annotationText.length > 500) return; // Máximo 500 caracteres

    setIsSaving(true);

    // Simular delay async (en futuro será llamada a API)
    setTimeout(() => {
      if (editingAnnotation) {
        // Actualizar anotación existente
        setAnnotations((prev) =>
          prev.map((a) =>
            a.id === editingAnnotation.id
              ? { ...a, text: annotationText, category: selectedCategory, updatedAt: new Date() }
              : a
          )
        );
        setSuccessMessage('Anotación actualizada exitosamente');
      } else if (tempCoordinates) {
        // Crear nueva anotación
        const newAnnotation: Annotation = {
          id: `annotation-${Date.now()}`,
          coordinates: tempCoordinates,
          text: annotationText,
          category: selectedCategory,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setAnnotations((prev) => [...prev, newAnnotation]);
        setSuccessMessage('Anotación creada exitosamente');
      }

      // Cerrar modal
      setShowModal(false);
      setEditingAnnotation(null);
      setTempCoordinates(null);
      setAnnotationText('');
      setSelectedCategory('note');
      setIsSaving(false);
    }, 300);
  };

  /**
   * Editar anotación
   */
  const handleEditAnnotation = (annotation: Annotation): void => {
    setEditingAnnotation(annotation);
    setAnnotationText(annotation.text);
    setSelectedCategory(annotation.category);
    setShowModal(true);
  };

  /**
   * Mostrar modal de confirmación para eliminar anotación
   */
  const handleDeleteAnnotation = (id: string): void => {
    setDeletingAnnotationId(id);
    setShowDeleteConfirm(true);
  };

  /**
   * Confirmar eliminación de anotación
   */
  const confirmDeleteAnnotation = (): void => {
    if (!deletingAnnotationId) return;

    setIsDeleting(true);

    // Simular delay async (en futuro será llamada a API)
    setTimeout(() => {
      setAnnotations((prev) => prev.filter((a) => a.id !== deletingAnnotationId));
      setShowModal(false);
      setEditingAnnotation(null);
      setShowDeleteConfirm(false);
      setDeletingAnnotationId(null);
      setIsDeleting(false);
      setSuccessMessage('Anotación eliminada exitosamente');
    }, 300);
  };

  /**
   * Navegar a anotación
   */
  const handleGoToAnnotation = (annotation: Annotation): void => {
    if (!map) return;

    map.flyTo({
      center: annotation.coordinates,
      zoom: 16,
      duration: 1500,
    });
  };

  /**
   * Mostrar modal de confirmación para limpiar todas
   */
  const handleClearAll = (): void => {
    setShowClearAllConfirm(true);
  };

  /**
   * Confirmar limpieza de todas las anotaciones
   */
  const confirmClearAll = (): void => {
    setIsClearing(true);

    // Simular delay async (en futuro será llamada a API)
    setTimeout(() => {
      setAnnotations([]);
      localStorage.removeItem('wayline-annotations');
      localStorage.removeItem('westeer-annotations');
      setShowClearAllConfirm(false);
      setIsClearing(false);
      setSuccessMessage('Todas las anotaciones han sido eliminadas');
    }, 300);
  };

  if (!isActive) return null;

  return (
    <>
      {/* Panel lateral con lista de anotaciones */}
      <div className="annotations-panel">
        <div className="annotations-header">
          <div className="annotations-title-wrapper">
            <StickyNote size={16} />
            <h3 className="annotations-title">Anotaciones</h3>
            <span className="annotations-count">{annotations.length}</span>
          </div>
          <button className="annotations-close" onClick={onDeactivate} aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div className="annotations-search">
          <div className="search-input-wrapper">
            <Search size={14} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Buscar anotaciones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Buscar anotaciones"
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Limpiar búsqueda"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="annotations-filters">
            {/* Filtro de categorías */}
            <select
              className="filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as any)}
              aria-label="Filtrar por categoría"
            >
              <option value="all">Todas</option>
              {Object.values(ANNOTATION_CATEGORIES).map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Toggle ordenamiento */}
            <button
              className="sort-button"
              onClick={() => setSortBy(sortBy === 'date' ? 'category' : 'date')}
              title={sortBy === 'date' ? 'Ordenar por categoría' : 'Ordenar por fecha'}
              aria-label={sortBy === 'date' ? 'Ordenar por categoría' : 'Ordenar por fecha'}
            >
              {sortBy === 'date' ? <Calendar size={14} /> : <Tag size={14} />}
            </button>
          </div>
        </div>

        <div className="annotations-content">
          {annotations.length === 0 && (
            <p className="annotations-empty">
              Haz clic en el mapa para agregar una anotación
            </p>
          )}

          {filteredAnnotations.length === 0 && annotations.length > 0 && (
            <p className="annotations-empty">
              No se encontraron anotaciones con los filtros aplicados
            </p>
          )}

          {filteredAnnotations.map((annotation) => {
            const categoryMeta = ANNOTATION_CATEGORIES[annotation.category];
            const IconComponent = {
              StickyNote,
              Lightbulb,
              CheckSquare,
              AlertCircle,
              AlertTriangle
            }[categoryMeta.icon];

            return (
              <div
                key={annotation.id}
                className="annotation-item"
                data-category={annotation.category}
                style={{
                  '--item-color': categoryMeta.color,
                  '--item-bg': categoryMeta.bgColor,
                  '--item-border': categoryMeta.borderColor
                } as React.CSSProperties}
              >
                {/* Badge de categoría */}
                <div className="annotation-category-badge">
                  {IconComponent && <IconComponent size={12} />}
                  <span>{categoryMeta.label}</span>
                </div>

                {/* Preview del texto (primeros 80 caracteres) */}
                <p className="annotation-preview">
                  {annotation.text.length > 80
                    ? `${annotation.text.substring(0, 80)}...`
                    : annotation.text
                  }
                </p>

                {/* Metadata: fecha y coordenadas */}
                <div className="annotation-metadata">
                  <span className="annotation-date" title={annotation.updatedAt.toLocaleString()}>
                    <Calendar size={11} />
                    {formatRelativeTime(annotation.updatedAt)}
                  </span>
                  <button
                    className={`annotation-coords-copy ${copiedCoordId === annotation.id ? 'copied' : ''}`}
                    onClick={() => copyCoordinates(annotation.coordinates, annotation.id)}
                    title="Copiar coordenadas"
                  >
                    <MapPin size={11} />
                    <Copy size={9} className="copy-icon" />
                  </button>
                </div>

                {/* Acciones */}
                <div className="annotation-item-actions">
                  <button
                    className="annotation-item-btn"
                    onClick={() => handleGoToAnnotation(annotation)}
                    title="Ir a ubicación"
                  >
                    <Navigation size={14} />
                  </button>
                  <button
                    className="annotation-item-btn"
                    onClick={() => handleEditAnnotation(annotation)}
                    title="Editar"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    className="annotation-item-btn annotation-item-btn-delete"
                    onClick={() => handleDeleteAnnotation(annotation.id)}
                    title="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {annotations.length > 0 && (
          <div className="annotations-footer">
            <div className="annotations-footer-info">
              {filteredAnnotations.length !== annotations.length && (
                <span className="filter-info">
                  Mostrando {filteredAnnotations.length} de {annotations.length}
                </span>
              )}
            </div>
            <button className="annotations-clear-btn" onClick={handleClearAll}>
              <Trash2 size={14} />
              <span>Limpiar todas</span>
            </button>
          </div>
        )}
      </div>

      {/* Modal para crear/editar anotación */}
      {showModal && (
        <div className="annotation-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="annotation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="annotation-modal-header">
              <h3 className="annotation-modal-title">
                {editingAnnotation ? 'Editar Anotación' : 'Nueva Anotación'}
              </h3>
              <button
                className="annotation-modal-close"
                onClick={() => setShowModal(false)}
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            <div className="annotation-modal-content">
              {/* Selector de categoría */}
              <div className="annotation-category-selector">
                <label className="annotation-category-label">Categoría</label>
                <div className="annotation-category-grid">
                  {Object.values(ANNOTATION_CATEGORIES).map((category) => {
                    const IconComponent = {
                      StickyNote,
                      Lightbulb,
                      CheckSquare,
                      AlertCircle,
                      AlertTriangle
                    }[category.icon];

                    return (
                      <button
                        key={category.id}
                        type="button"
                        className={`annotation-category-option ${selectedCategory === category.id ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category.id)}
                        style={{
                          '--category-color': category.color,
                          '--category-border': category.borderColor,
                          '--category-bg': category.bgColor
                        } as React.CSSProperties}
                        title={category.description}
                      >
                        {IconComponent && <IconComponent size={20} />}
                        <span>{category.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Textarea con contador */}
              <div className="annotation-textarea-wrapper">
                <label className="annotation-textarea-label">
                  Texto de la anotación
                  <span className="annotation-char-counter">
                    {annotationText.length}/500
                  </span>
                </label>
                <textarea
                  className="annotation-modal-textarea"
                  placeholder="Escribe tu anotación aquí..."
                  value={annotationText}
                  onChange={(e) => setAnnotationText(e.target.value.slice(0, 500))}
                  maxLength={500}
                  autoFocus
                  rows={6}
                />
              </div>
            </div>

            <div className="annotation-modal-actions">
              <button
                className="annotation-modal-btn annotation-modal-btn-primary"
                onClick={handleSaveAnnotation}
                disabled={!annotationText.trim() || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="spinner" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Guardar</span>
                  </>
                )}
              </button>
              <button
                className="annotation-modal-btn annotation-modal-btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              {editingAnnotation && (
                <button
                  className="annotation-modal-btn annotation-modal-btn-danger"
                  onClick={() => handleDeleteAnnotation(editingAnnotation.id)}
                >
                  <Trash2 size={16} />
                  <span>Eliminar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para eliminar anotación individual */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingAnnotationId(null);
        }}
        onConfirm={confirmDeleteAnnotation}
        title="Eliminar Anotación"
        message="¿Estás seguro de que deseas eliminar esta anotación? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Modal de confirmación para limpiar todas las anotaciones */}
      <ConfirmModal
        isOpen={showClearAllConfirm}
        onClose={() => setShowClearAllConfirm(false)}
        onConfirm={confirmClearAll}
        title="Limpiar Todas las Anotaciones"
        message={`¿Estás seguro de eliminar TODAS las anotaciones (${annotations.length} en total)? Esta acción no se puede deshacer.`}
        confirmText="Eliminar Todas"
        cancelText="Cancelar"
        variant="danger"
        isLoading={isClearing}
      />

      {/* Toast de éxito */}
      <SuccessToast
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
      />
    </>
  );
}
